import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Clock, Eye, ArrowRight, Loader2 } from 'lucide-react';
import SEO from '../utils/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPage = () => {
  const [searchParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const page = parseInt(searchParams.get('page') || '1');
  const tag = searchParams.get('tag') || '';

  useEffect(() => {
    document.title = 'PosterFire Blog — Poster News, Art Inspiration & Tips';
    SEO.setCanonical(`${SEO.CANONICAL_BASE}/blog`);
    SEO.setRobots(tag || page > 1 ? 'noindex, follow' : 'index, follow');
    SEO.setMeta('meta[name="description"]', 'content', 'Read the PosterFire blog for poster collecting tips, wall art inspiration, and the latest in movie, music and sports poster news.');
    return () => SEO.resetDefaults();
  }, [tag, page]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 12 });
    if (tag) params.set('tag', tag);
    axios.get(`${API}/blog/posts?${params}`).then(r => {
      setPosts(r.data.posts);
      setTotal(r.data.total);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [page, tag]);

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      {/* Hero */}
      <div className="bg-gray-950 text-white py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-fire mb-4 font-semibold">PosterFire Blog</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mb-4">
            Wall Art Inspiration & Tips
          </h1>
          <p className="text-lg text-white/60 max-w-2xl mx-auto">
            Decorating ideas, poster styling guides, and the latest trends in wall art and home decor.
          </p>
        </div>
      </div>

      {/* Posts */}
      <div className="max-w-6xl mx-auto px-4 py-12 md:py-16">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-300" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">No Posts Yet</h2>
            <p className="text-gray-500">Check back soon for articles on wall art, home decor, and poster styling tips.</p>
          </div>
        ) : (
          <>
            {/* Featured Post (first) */}
            {posts.length > 0 && (
              <Link to={`/blog/${posts[0].slug}`} className="group block mb-12" data-testid="featured-post">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  {posts[0].cover_image && (
                    <div className="overflow-hidden rounded-2xl">
                      <img src={posts[0].cover_image} alt={posts[0].title}
                        className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className={posts[0].cover_image ? '' : 'md:col-span-2'}>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {(posts[0].tags || []).slice(0, 3).map((t, i) => (
                        <span key={i} className="px-3 py-1 text-xs font-semibold uppercase tracking-wider bg-fire/10 text-fire rounded-full">{t}</span>
                      ))}
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-3 group-hover:text-fire transition-colors leading-tight">
                      {posts[0].title}
                    </h2>
                    <p className="text-gray-500 text-base leading-relaxed mb-4 line-clamp-3">{posts[0].excerpt}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-400">
                      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{formatDate(posts[0].published_at)}</span>
                      <span className="flex items-center gap-1.5"><Eye className="w-3.5 h-3.5" />{(posts[0].views || 0).toLocaleString()} views</span>
                    </div>
                  </div>
                </div>
              </Link>
            )}

            {/* Grid */}
            {posts.length > 1 && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.slice(1).map((post) => (
                  <Link key={post.id} to={`/blog/${post.slug}`} className="group" data-testid={`blog-card-${post.slug}`}>
                    <article className="h-full flex flex-col">
                      {post.cover_image && (
                        <div className="overflow-hidden rounded-xl mb-4">
                          <img src={post.cover_image} alt={post.title}
                            className="w-full aspect-[16/10] object-cover group-hover:scale-105 transition-transform duration-500" />
                        </div>
                      )}
                      <div className="flex flex-wrap gap-1.5 mb-2">
                        {(post.tags || []).slice(0, 2).map((t, i) => (
                          <span key={i} className="px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider bg-gray-100 text-gray-600 rounded-full">{t}</span>
                        ))}
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-fire transition-colors mb-2 line-clamp-2 leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3 flex-1">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-400">
                        <span>{formatDate(post.published_at)}</span>
                        <span className="flex items-center gap-1 text-fire font-semibold group-hover:gap-2 transition-all">
                          Read <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
